import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const GameCharacterContainer = styled.div`
  position: fixed;
  bottom: 50px;
  left: 50px;
  z-index: 1000;
  pointer-events: none;

  @media (max-width: 768px) {
    bottom: 15px;
    left: 15px;
  }
`;

const Character = styled(motion.div)`
  width: 62px;
  height: 62px;
  background: rgba(26, 26, 26, 0.7);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.785rem;
  border: 2px solid var(--color-primary);
  box-shadow: 0 0 15px rgba(255, 144, 82, 0.5);
  pointer-events: auto;
  color: rgba(0, 0, 0, 0.78);
  cursor: pointer;
  position: relative;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
    font-size: 2.2rem;
  }
`;

const SpeechBubble = styled(motion.div)`
  position: absolute;
  bottom: 100%;
  left: -70%;
  min-width: 140px;
  transform: translateX(-50%);
  background: rgba(26, 26, 26, 0.9);
  border-radius: 10px;
  padding: 0.8rem 1rem;
  color: var(--color-white);
  font-size: 0.9rem;
  max-width: 320px;
  border: 1px solid var(--color-primary);
  box-shadow: 0 0 10px rgba(255, 144, 82, 0.3);

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid rgba(26, 26, 26, 0.9);
  }

  @media (max-width: 768px) {
    max-width: 320px;
    padding: 1rem 1.2rem;
    font-size: 1rem;
    top: -100px;
    left: 0;
    transform: translateX(0);

    &::after {
      left: 30px;
      transform: translateX(0);
    }
  }

  @media (max-width: 480px) {
    max-width: 320px;
    padding: 1.2rem 1.4rem;
    font-size: 1.1rem;
    left: -10px;
  }
`;

const GameCharacter: React.FC = () => {
  const [showBubble, setShowBubble] = useState(false);
  const [currentTip, setCurrentTip] = useState("");
  const [currentSection, setCurrentSection] = useState("");

  const tips = {
    default: [
      "Hello! Tap on the shiny items to collect points!",
      "Explore all sections to discover more about me!",
      "Try to find all the hidden achievements!",
      "Swipe to navigate the site faster!",
      "Tip: Visit the Achievements section to track your progress!",
    ],
    journey: [
      "This timeline shows my professional journey!",
      "Each experience helped me develop new skills!",
      "My journey began in 2016 and continues today!",
    ],
    skills: [
      "These are my main technical competencies!",
      "You can filter by category to see specific skills!",
      "The bars show my proficiency level in each skill!",
    ],
    projects: [
      "These are some of the projects I've developed!",
      "Tap the links to see more details about each project!",
      "Projects are organized by category for easy navigation!",
    ],
    experiences: [
      "Here are my most relevant professional experiences!",
      "Each card shows my key achievements and technologies used!",
      "My experience covers different sectors and project types!",
    ],
    achievements: [
      "Unlock achievements by exploring the site!",
      "Each achievement represents an important skill or accomplishment!",
      "Try to unlock all achievements to reach the highest level!",
    ],
    contact: [
      "Get in touch to discuss projects or opportunities!",
      "Fill out the form, and I'll respond as soon as possible!",
      "You can also find me on the social networks listed here!",
    ],
  };

  // Detectar a seção atual com base na URL
  useEffect(() => {
    const detectSection = () => {
      const path = window.location.pathname;

      if (path.includes("journey")) setCurrentSection("journey");
      else if (path.includes("skills")) setCurrentSection("skills");
      else if (path.includes("projects")) setCurrentSection("projects");
      else if (path.includes("experiences")) setCurrentSection("experiences");
      else if (path.includes("achievements")) setCurrentSection("achievements");
      else if (path.includes("contact")) setCurrentSection("contact");
      else setCurrentSection("default");
    };

    detectSection();

    // Mostrar dica aleatória quando o personagem aparece pela primeira vez
    showRandomTip();

    // Mostrar dica aleatória a cada 60 segundos
    const interval = setInterval(showRandomTip, 60000);

    return () => clearInterval(interval);
  }, [currentSection]);

  // Mostrar dica aleatória
  const showRandomTip = () => {
    const sectionTips =
      tips[currentSection as keyof typeof tips] || tips.default;
    const randomTip =
      sectionTips[Math.floor(Math.random() * sectionTips.length)];

    setCurrentTip(randomTip);
    setShowBubble(true);

    // Esconder após 5 segundos
    setTimeout(() => {
      setShowBubble(false);
    }, 5000);
  };

  // Alternar exibição da dica ao clicar no personagem
  const toggleTip = () => {
    if (showBubble) {
      setShowBubble(false);
    } else {
      showRandomTip();
    }
  };

  // Detectar gestos de swipe para dispositivos móveis
  useEffect(() => {
    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      touchEndX = e.changedTouches[0].clientX;
      handleSwipe();
    };

    const handleSwipe = () => {
      // Detectar swipe da esquerda para direita (próximo do personagem)
      if (touchEndX - touchStartX > 100 && touchStartX < 150) {
        showRandomTip();
      }
    };

    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);

  return (
    <GameCharacterContainer>
      <Character
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={toggleTip}
      >
        💡
        {showBubble && (
          <SpeechBubble
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.3 }}
          >
            {currentTip}
          </SpeechBubble>
        )}
      </Character>
    </GameCharacterContainer>
  );
};

export default GameCharacter;
